<template>
  <ButtonSquare
    :title="$t('general__view__vector-layer')"
    :imgSource="iconMaps"
    size="large"
    :activeState="activeState"
    :hoverButtons="hoverButtons"
    :activeHoverButtonValue="currentMapVectorTileId"
    :showLoadingSpinner="mapVectorTileState === 'loading'"
    @clicked="toggle"
    @onHoverButtonClick="changeVectorTile"
    @mouseover="$emit('MapVectorTileButtonHovered')"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import ButtonSquare from '@/components/general/buttons/ButtonSquare/ButtonSquare.vue'
import iconMaps from '@/assets/icon-maps.svg'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { SET_CURRENT_MAP_VECTOR_TILE_ID, SET_MAP_VECTOR_TILE_STATE } from '@/store/mutation-types'

defineEmits(['MapVectorTileButtonHovered'])
const store = useStore()

// Vector tile definitions (demo for AH Umeå)
const vectorTiles = [{ title: 'Umeå Energi – Ledningar', id: 'vektorlager_1' }]

// State from Vuex
const selectedRealEstate = computed(() => store.state[MAPVIEW_PREFIX].selectedRealEstate)
const currentMapVectorTileId = computed(() => store.state[MAPVIEW_PREFIX].currentMapVectorTileId)
const mapVectorTileState = computed(() => store.state[MAPVIEW_PREFIX].mapVectorTileState)

// Hover buttons for the vector tile selection
const hoverButtons = ref<{ title: string; emit: string }[]>([])

// Compute active state for the button
const activeState = computed(() => {
  if (!hoverButtons.value.length) return 'deactivated'
  return mapVectorTileState.value !== 'hidden' ? 'active' : 'inactive'
})

// Watch for changes in selectedRealEstate to update hoverButtons and state
watch(
  selectedRealEstate,
  () => {
    setMapVectorTileState('hidden')
    hoverButtons.value = getHoverButtons()
    if (hoverButtons.value.length) setCurrentMapVectorTileId(hoverButtons.value[0].emit)
  },
  { immediate: true }
)

// Helper to get hover buttons based on selected real estate
function getHoverButtons() {
  const buttons: { title: string; emit: string }[] = []
  if (!selectedRealEstate.value) return buttons
  // Frontend demo for AH Umeå, currently real estate id 727903614376603648 on both dev and live
  if (selectedRealEstate.value.id === '727903614376603648') {
    vectorTiles.forEach((vectorTile) => {
      buttons.push({ title: vectorTile.title, emit: vectorTile.id })
    })
  }
  return buttons
}

// Mutations
function setCurrentMapVectorTileId(id: string) {
  store.commit(`${MAPVIEW_PREFIX}/${SET_CURRENT_MAP_VECTOR_TILE_ID}`, id)
}
function setMapVectorTileState(state: string) {
  store.commit(`${MAPVIEW_PREFIX}/${SET_MAP_VECTOR_TILE_STATE}`, state)
}

// Change the selected vector tile
function changeVectorTile(id: string) {
  if (id === currentMapVectorTileId.value && mapVectorTileState.value === 'visible') return
  setCurrentMapVectorTileId(id)
  setMapVectorTileState('loading')
}

// Toggle visibility of the vector tile layer
function toggle() {
  if (activeState.value === 'deactivated') return
  if (hoverButtons.value.length && !currentMapVectorTileId.value)
    setCurrentMapVectorTileId(hoverButtons.value[0].emit)
  const newMapVectorTileState = mapVectorTileState.value === 'hidden' ? 'loading' : 'hidden'
  setMapVectorTileState(newMapVectorTileState)
}
</script>
